import {orderBy} from 'lodash'
import {generateUUID} from '~/helpers/uuid'
import {SORTS} from '~/constants/assortments'
import {useAssortmentsStore} from '~/stores/assortments'
import type {Assortment, BioSort, SummaryDetailAssortmentGroup} from '~/models/assortments'
import type {ContractDetail, FullContract} from '~/models/contract'
import type {Favorite} from '~/models/favorites'
import type {Option, OptionList} from '~/models/util'
import {notNil} from '~/helpers/notNil'
import type {
    GmModelLibraryModelsAssortmentModel,
    GmModelLibraryModelsContractsTimberPriceListType
} from "~/gen/openapi/sblService";
import {usePriceListService} from "~/services/priceListService";
import {queryClient} from "~/plugins/vue-query";

export const convertAssortmentToContractDetail = (assortment: Assortment, priceType: number, period: string | null = null): ContractDetail => {
  const detail: ContractDetail = {
    Id: 'temp_' + generateUUID(),
    AssortmentId: assortment.Id,
    Quantity: 0,
    Percent: 0,
    PriceType: priceType,
    Period: period,
    UseAboveBelowPrice: false,
    WoodSpecies: [],
    Assortment: {
      Id: assortment.Id,
      Name: assortment.AssortmentName,
      Number: assortment.AssortmentNo,
      EndProductCode: assortment.EndProductCode,
    }
  }

  return detail
}

// Group assortments by MainAssortmentGroup and convert to option
export const getGroupedAssortmentOptions = (assortments: Ref<Array<GmModelLibraryModelsAssortmentModel>>): OptionList[] => {
  const options = [] as OptionList[]
  const orderedAssortments = orderBy(assortments.value, 'AssortmentNo')

  orderedAssortments.forEach((assortment: Assortment) => {
    const existingGroup = options.find(x => x.key.toString().includes(assortment.MainAssortmentGroupId)) || null
    if (!existingGroup) {
      options.push({
        key: assortment.MainAssortmentGroupId,
        text: assortment.MainAssortmentGroupName,
        options: [{
          key: assortment.Id,
          value: assortment.Id,
          text: `${assortment.AssortmentNo} ${assortment.AssortmentName}`
        }]
      })
    } else {
      existingGroup.options.push({
        key: assortment.Id,
        value: assortment.Id,
        text: `${assortment.AssortmentNo} ${assortment.AssortmentName}`
      })
    }
  })

  return options
}

export const assortmentToOption = (assortment: Assortment): Option => {
  return {
    key: assortment.Id,
    value: assortment.Id,
    text: `${assortment.AssortmentNo} ${assortment.AssortmentName}`,
    subText: assortment.MainAssortmentGroupName
  }
}

export const getAssortmentOptions = (assortments: Assortment[]): Option[] =>
  assortments.map(assortmentToOption)

export const getFavoriteOptions = (assortments: Ref<Array<GmModelLibraryModelsAssortmentModel>>): Option[] => {
    const assortmentsStore = useAssortmentsStore()
    return assortmentsStore.favoriteAssortments
    .map((favorite: Favorite) => {
      const assortment =
        assortments.value?.find((x: Assortment) => x.Id === favorite.id) || null
      if (assortment) {
        return {
          key: assortment.Id,
          value: assortment.Id,
          text: `${assortment.AssortmentNo} ${assortment.AssortmentName}`,
          subText: assortment.MainAssortmentGroupName
        }
      } else {
        return null
      }
    })
    .filter(notNil)
}

export const getBioSortsOptions = (): Option[] => {
  return SORTS.map((sort: BioSort) => {
    return {
      key: sort.Id,
      value: sort.Guid.toLowerCase(),
      text: sort.Name
    }
  })
}

export const getPriceTypeOptions = () => {
  const { priceTypesQueryOptions } = usePriceListService()
  const priceTypes = (queryClient.getQueryData(priceTypesQueryOptions.queryKey) || []) as GmModelLibraryModelsContractsTimberPriceListType[]
  return priceTypes?.map((priceType: GmModelLibraryModelsContractsTimberPriceListType) => {
    return {
      key: priceType.Id,
      value: priceType.Id,
      text: priceType.Label
    }
  })
}

export const getPriceTypeLabel = (id: number): string => {
  const { priceTypesQueryOptions } = usePriceListService()
  const priceTypes = (queryClient.getQueryData(priceTypesQueryOptions.queryKey) || []) as GmModelLibraryModelsContractsTimberPriceListType[]
  return priceTypes.find(p => p.Id === id) ? priceTypes.find(p => p.Id === id)?.Label! : ''
}

export const getContractDetailsMergedByAssortmentGroup = (contract: FullContract, assortments: Ref<Array<GmModelLibraryModelsAssortmentModel>>): SummaryDetailAssortmentGroup[] => {
  if (!contract.Details) {
    return []
  }
  return contract.Details.map((detail) => {
    const assortment = assortments.value?.find(a => a.Id === detail.AssortmentId)
    return {
      ...(assortment || {}),
      ...detail,
    }
  }).reduce((acc, v) => {
    const existingGroup = acc.find(x => x.GroupName === v.MainAssortmentGroupName)
    if (existingGroup) {
      existingGroup.Quantity += v.Quantity
      existingGroup.Assortments.push(v)
    } else {
      acc.push({
        GroupName: v.MainAssortmentGroupName,
        SortNo: v.MainAssortmentGroupSortNo || 99999,
        Quantity: v.Quantity,
        Assortments: [v]
      })
    }
    return acc
  }, [] as SummaryDetailAssortmentGroup[])
    .map((group) => {
      return {
        ...group,
        Assortments: orderBy(group.Assortments, 'AssortmentNo')
      }
    })
    .sort((a, b) => a.SortNo - b.SortNo)
}

export const getAssortmentNoById = (id: string, assortments: Array<Assortment>): number|string => {
  const assortment: Assortment = assortments.find(a => a.Id === id)!

  return assortment ? assortment.AssortmentNo! : ''
}

export const getAssortmentNameById = (id: string, assortments: Array<Assortment>): string => {
  const assortment: Assortment = assortments.find(a => a.Id === id)!

  return assortment ? assortment.AssortmentName! : ''
}

export const findAssortmentById = (assortments: Ref<Array<GmModelLibraryModelsAssortmentModel>>, id?: string): Assortment | undefined => {
  return assortments.value?.find((assortment) => assortment.Id === id)
}

export const requiresSubType = (contractDetail: ContractDetail, assortments: Ref<Array<GmModelLibraryModelsAssortmentModel>>): boolean => {
  const assortment = findAssortmentById(assortments, contractDetail.AssortmentId!)
  return !!assortment?.SpecifyEuWoodSpecies
}